import type { Card, CardColor, CardKind } from '../cards.js'
import type { LocaleTexts, LogMessage, NameOf } from '../i18n.js'
import type { Army } from '../state.js'

/**
 * Hindi texts produced by the server (game log, errors).
 * Player names are arbitrary usernames of unknown gender: sentences use
 * "ने" + perfective (the verb agrees with the object), "की"/"के पास"
 * constructions, passives, or the respectful plural, which reads as neutral.
 */

const cardColors: Record<CardColor, string> = { red: 'लाल', blue: 'नीला', green: 'हरा', yellow: 'पीला' }

const cardKinds: Record<CardKind, string> = {
  number: 'नंबर',
  zero: '0',
  skip: 'स्किप',
  reverse: 'रिवर्स',
  plus2: '+2',
  wild: 'जोकर',
  wild4: '+4',
}

function cardLabel(card: Card): string {
  const color = card.color ? cardColors[card.color] : ''
  switch (card.kind) {
    case 'number':
      return `${color} ${card.value}`
    case 'zero':
      return `${color} 0`
    case 'skip':
    case 'reverse':
    case 'plus2':
      return `${color} ${cardKinds[card.kind]}`
    case 'wild':
    case 'wild4':
      return cardKinds[card.kind]
  }
}

const winReasons: LocaleTexts['winReasons'] = {
  king_captured: 'विरोधी राजा मारा गया',
  checkmate: 'शह और मात',
  resign: 'विरोधी ने हार मान ली',
  abandon: 'विरोधी ने गेम छोड़ दिया (लंबे समय तक डिस्कनेक्ट या निष्क्रिय)',
}

/** "1 चाल", "3 चालें". */
function moves(count: number): string {
  return `${count} ${count === 1 ? 'चाल' : 'चालें'}`
}

function log(m: LogMessage, name: NameOf, card: (cardId: string) => string): string {
  const army = (a: Army) => (a === 'w' ? 'सफ़ेद' : 'काले')
  switch (m.id) {
    case 'game_start':
      return `गेम शुरू: सफ़ेद मोहरे ${name(m.white)} के पास, काले मोहरे ${name(m.black)} के पास।`
    case 'deck_reshuffled':
      return `डिस्कार्ड ढेर को फेंटकर नई गड्डी बनाई गई (${m.count} कार्ड)।`
    case 'deck_empty':
      return `${name(m.player)} कार्ड नहीं खींच सकते: कोई कार्ड नहीं बचा।`
    case 'card_drawn':
      return `${name(m.player)} ने ${card(m.cardId)} खींचा और सबको दिखाया।`
    case 'turn_start':
      return `बारी ${m.turn}: ${name(m.player)} ${army(m.army)} मोहरों से खेलेंगे।`
    case 'number_card':
      return `${name(m.player)} इस बारी में ${moves(m.moves)} चलेंगे।`
    case 'zero_card':
      return `0: ${name(m.player)} की बारी बिना किसी चाल के निकल गई।`
    case 'skip_card':
      return `स्किप: ${name(m.player)} की बारी गई, अब ${name(m.next)} कार्ड खींचकर खेलेंगे।`
    case 'skip_in_check':
      return `स्किप: ${name(m.player)} पर शह है, इसलिए बारी नहीं छोड़ी जा सकती; बचाव के लिए 1 चाल चलनी होगी।`
    case 'recovery_in_check':
      return `${name(m.player)} पर अब भी शह है: बचाव के लिए 1 चाल चलनी होगी।`
    case 'no_pawn_to_recover':
      return `${card(m.cardId)}: ${army(m.army)} के लिए वापस पाने को कोई प्यादा नहीं${m.noSquare ? ' (कोई खाली खाना नहीं)' : ''}।`
    case 'pawns_recovered':
      return m.count === 1
        ? `${card(m.cardId)}: ${name(m.player)} को बोर्ड पर रखने के लिए 1 प्यादा वापस मिला।`
        : `${card(m.cardId)}: ${name(m.player)} को बोर्ड पर रखने के लिए ${m.count} प्यादे वापस मिले।`
    case 'reverse_card':
      return `रिवर्स: अब सफ़ेद मोहरे ${name(m.white)} के पास और काले मोहरे ${name(m.black)} के पास। ${name(m.player)} ${army(m.army)} मोहरों से खेलेंगे।`
    case 'joker_card':
      return `जोकर: ${name(m.player)} कोई भी मोहरा वज़ीर की तरह चल सकते हैं (1 चाल)।`
    case 'effect_chosen':
      return `${name(m.player)} ने ${moves(m.moves)} ${m.moves === 1 ? 'चुनी' : 'चुनीं'}।`
    case 'pawn_placed':
      return `${name(m.player)} ने ${m.square} पर एक प्यादा रखा।`
    case 'no_move_turn_ends':
      return `${name(m.player)} के पास कोई चाल नहीं: बारी खत्म।`
    case 'turn_end':
      return `बारी ${m.turn} खत्म।`
    case 'draw_agreed':
      return 'ड्रॉ: दोनों खिलाड़ी ड्रॉ पर राज़ी हुए।'
    case 'draw_no_moves':
      return `ड्रॉ: लगातार ${m.turns} बारियों में कोई मोहरा नहीं चला।`
    case 'win':
      return `${name(m.winner)} की जीत — ${winReasons[m.reason] ?? m.reason}।`
    case 'move':
      return `${name(m.player)} (${army(m.army)}) ने ${m.san} चला।`
    case 'series_stopped_threat':
      return 'विरोधी राजा पर हमला है: चालों की सीरीज़ यहीं रुक गई।'
    case 'no_move_left':
      return `${name(m.player)} के पास अब कोई चाल नहीं बची।`
    case 'series_ended':
      return `${name(m.player)} ने अपनी चालों की सीरीज़ खत्म की।`
    case 'draw_offered':
      return `${name(m.player)} ने ड्रॉ का प्रस्ताव दिया।`
    case 'draw_declined':
      return `${name(m.player)} ने ड्रॉ का प्रस्ताव ठुकरा दिया।`
  }
}

export const hi: LocaleTexts = {
  cardColors,
  cardKinds,
  cardLabel,
  winReasons,
  log,
  errors: {
    game_over: 'गेम खत्म हो चुका है।',
    server_only: 'यह कमांड सिर्फ़ सर्वर के लिए है।',
    forfeit_needs_player: 'फ़ॉरफ़िट में खिलाड़ी का नाम होना ज़रूरी है (applyForfeit इस्तेमाल करें)।',
    draw_already_offered: 'ड्रॉ का एक प्रस्ताव पहले से बाकी है।',
    no_draw_to_accept: 'स्वीकार करने के लिए ड्रॉ का कोई प्रस्ताव नहीं है।',
    no_draw_to_decline: 'ठुकराने के लिए ड्रॉ का कोई प्रस्ताव नहीं है।',
    not_your_turn: 'अभी आपकी बारी नहीं है।',
    no_effect_pending: 'कोई असर बाकी नहीं है।',
    no_effect_to_choose: 'चुनने के लिए कोई असर नहीं है।',
    choose_moves: (p) => `1 से ${p.max} के बीच चालें चुनें।`,
    no_pawn_to_place: 'रखने के लिए कोई प्यादा नहीं है।',
    invalid_pawn_square: 'इस खाने पर प्यादा नहीं रखा जा सकता।',
    cannot_place_pawn: 'प्यादा यहाँ नहीं रखा जा सकता।',
    not_move_phase: 'अभी चाल चलने का चरण नहीं है।',
    invalid_move: 'यह चाल मान्य नहीं है।',
    king_attacked_square: 'राजा ऐसे खाने पर नहीं जा सकता जिस पर हमला हो।',
    king_left_in_check: 'इस चाल से आपके राजा पर शह रह जाएगी।',
    king_not_capturable: 'शुद्ध मोड में राजा को मारा नहीं जा सकता।',
    first_move_required: 'पहली चाल चलना ज़रूरी है।',
    no_series: 'चालों की कोई सीरीज़ नहीं चल रही।',
    unknown_command: 'अनजान कमांड।',
    invite_unknown: 'यह इनवाइट कोड मौजूद नहीं है।',
    game_not_found: 'गेम नहीं मिला।',
    game_full: 'यह गेम पहले से भरा हुआ है।',
    not_in_game: 'आप इस गेम में नहीं खेल रहे हैं।',
    game_not_started: 'गेम अभी शुरू नहीं हुआ है।',
    game_not_finished: 'गेम अभी खत्म नहीं हुआ है।',
    stale_command: (p) => `पुरानी कमांड: अपेक्षित वर्ज़न ${p.expected}, मिला ${p.received}।`,
    command_id_reused: 'यह कमांड ID पहले ही इस्तेमाल हो चुकी है।',
    view_unavailable: 'व्यू उपलब्ध नहीं है।',
    cannot_challenge_self: 'आप खुद को चुनौती नहीं दे सकते।',
    player_not_found: 'खिलाड़ी नहीं मिला।',
    rate_limited: 'बहुत ज़्यादा रिक्वेस्ट, थोड़ी देर में फिर कोशिश करें।',
    invalid_request: 'अमान्य रिक्वेस्ट।',
    internal: 'सर्वर में अंदरूनी गड़बड़ी।',
    server_unreachable: 'सर्वर जवाब नहीं दे रहा।',
    username_taken: 'यह यूज़रनेम पहले से लिया जा चुका है।',
    bad_credentials: 'यूज़रनेम या पासवर्ड गलत है।',
    too_many_attempts: 'बहुत ज़्यादा कोशिशें, एक मिनट बाद फिर कोशिश करें।',
    invalid_data: 'अमान्य डेटा।',
    username_format: 'यूज़रनेम: 3 से 20 अक्षर, अंक, “_”, “-” या “.”।',
    password_length: 'पासवर्ड: कम से कम 6 कैरेक्टर।',
    credentials_required: 'यूज़रनेम और पासवर्ड ज़रूरी हैं।',
    invalid_session: 'अमान्य सेशन।',
    match_not_ranked: 'अनरैंक्ड गेम।',
    login_failed: 'लॉग इन नहीं हो सका।',
    feedback_empty: 'कोई मैसेज लिखें या रेटिंग दें।',
    feedback_rate_limited: 'धन्यवाद! आप पहले ही कई मैसेज भेज चुके हैं: थोड़ी देर बाद फिर कोशिश करें।',
    tournament_unknown: 'यह टूर्नामेंट कोड मौजूद नहीं है।',
    tournament_started: 'यह टूर्नामेंट पहले ही शुरू हो चुका है।',
    tournament_full: 'यह टूर्नामेंट भर चुका है।',
    tournament_not_host: 'यह सिर्फ़ आयोजक कर सकते हैं।',
    tournament_not_open: 'इस टूर्नामेंट का रजिस्ट्रेशन बंद हो चुका है।',
    tournament_needs_players: 'टूर्नामेंट शुरू करने के लिए कम से कम 2 खिलाड़ी चाहिए।',
    tournament_over: 'यह टूर्नामेंट खत्म हो चुका है।',
    tournament_too_many: 'आप पहले से बहुत सारे टूर्नामेंट चला रहे हैं: पहले किसी एक को पूरा या रद्द करें।',
    tournament_no_rematch: 'टूर्नामेंट में रीमैच नहीं होता: आगे का खेल ब्रैकेट से जारी रखें।',
  },
}
